import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Loading from '../landing/layout/Loader';
import ReturnChatLoader from '../common/ReturnChatLoader';

const AuthGuard: React.FC = () => {
  const { isAuthenticated, loading, loginWithToken, loginType } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [isExpired, setIsExpired] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const hasProcessedToken = React.useRef(false);

  useEffect(() => {
    // We process the token if it exists AND we haven't processed it in this mount cycle.
    if (token && !hasProcessedToken.current) {
      hasProcessedToken.current = true;
      setIsProcessing(true);
      
      const result = loginWithToken(token);
      
      if (result.expired) {
        setIsExpired(true);
        setIsProcessing(false);
        return;
      }

      if (result.success) {
        // Clean up the URL by removing the token parameter
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('token');
        setSearchParams(newParams, { replace: true });
        
        // Minimize the delay. We just need one tick for the URL/Redux to propogate.
        // We use 50ms instead of 300ms for a snappier feel.
        setTimeout(() => {
          setIsProcessing(false);
        }, 50);
      } else {
        setIsProcessing(false);
      }
    }
  }, [token, loginWithToken, searchParams, setSearchParams]);

  if (isExpired) {
    return <Navigate to="/token-expired" replace />;
  }

  // Detect if we are on the return-chat page to avoid the heavy black loader
  const isReturnChatPath = /^\/customer\/return-chat(\/|$)/i.test(location.pathname);
  const isOrderDetailsPath = /^\/customer\/orders(\/|$)/i.test(location.pathname);

  // Show loading during auth context initialization OR magic link processing
  if (loading || isProcessing) {
    if (isReturnChatPath || isOrderDetailsPath) {
      return <ReturnChatLoader />;
    }
    return (
      <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center">
        <Loading />
      </div>
    );
  }

  // If there's a token but we're not yet authenticated (and not processing), we handle the fallback
  if (token && !isAuthenticated && !hasProcessedToken.current) {
    if (isReturnChatPath || isOrderDetailsPath) {
      return <ReturnChatLoader />;
    }
    return (
      <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center">
        <Loading />
      </div>
    );
  }

  // Basic authentication check
  if (!isAuthenticated) {
    return <Navigate to="/customer-login" state={{ from: location }} replace />;
  }

  /**
   * RESTRICTED ACCESS CHECK
   * If the user logged in via a token (Magic Link), 
   * they are only allowed to access the return-chat routes and order details routes.
   */
  // Strictly allow magic-link users to access return-chat, specific order details, and resolution sub-routes
  const isOrderSpecificPath = /^\/customer\/orders\//i.test(location.pathname) && location.pathname !== '/customer/orders';

  if (loginType === 'token') {
    if (!isReturnChatPath && !isOrderSpecificPath) {
      console.warn(`[AuthGuard] Restricted access redirect. Path: ${location.pathname}, loginType: ${loginType}`);
      return <Navigate to="/token-expired" replace />;
    }
  }

  return <Outlet />;
};

export default AuthGuard;
